import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Building2, Clock, MapPin, Phone, Globe, UtensilsCrossed,
  Users, Calendar, ShoppingBag, ChefHat, CheckCircle2, 
  ArrowRight, ArrowLeft, Save, Loader2, Plus, Trash2,
  DollarSign, Timer, AlertCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step indicators
const STEPS = [
  { id: 1, title: "Restaurant Profile", icon: Building2, description: "Basic info about your restaurant" },
  { id: 2, title: "Hours & Contact", icon: Clock, description: "Operating hours and contact details" },
  { id: 3, title: "Menu Setup", icon: UtensilsCrossed, description: "Add your menu items" },
  { id: 4, title: "Reservations", icon: Calendar, description: "Table and booking settings" },
  { id: 5, title: "Takeout & Orders", icon: ShoppingBag, description: "Pickup and pre-order settings" },
  { id: 6, title: "Review & Launch", icon: CheckCircle2, description: "Review and activate supervisors" },
];

// Cuisine types
const CUISINE_TYPES = [
  "American", "Italian", "Mexican", "Chinese", "Japanese", "Thai", 
  "Indian", "Mediterranean", "French", "Korean", "Vietnamese", 
  "Greek", "Spanish", "Middle Eastern", "Caribbean", "Other"
];

// Default hours template
const DEFAULT_HOURS = {
  monday: { open: "11:00", close: "22:00", closed: false },
  tuesday: { open: "11:00", close: "22:00", closed: false },
  wednesday: { open: "11:00", close: "22:00", closed: false },
  thursday: { open: "11:00", close: "22:00", closed: false },
  friday: { open: "11:00", close: "23:00", closed: false },
  saturday: { open: "11:00", close: "23:00", closed: false },
  sunday: { open: "11:00", close: "21:00", closed: false },
};

export default function RestaurantOnboarding() {
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Form data
  const [profile, setProfile] = useState({
    name: "",
    description: "",
    cuisineType: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    phone: "",
    email: "",
    website: "",
  });
  
  const [hours, setHours] = useState(DEFAULT_HOURS);
  
  const [menu, setMenu] = useState({
    categories: [
      { name: "Appetizers", items: [] },
      { name: "Main Courses", items: [] },
      { name: "Desserts", items: [] },
      { name: "Beverages", items: [] },
    ]
  });
  
  const [reservations, setReservations] = useState({
    enabled: true,
    maxPartySize: 10,
    minPartySize: 1,
    slotDuration: 90, // minutes
    advanceBookingDays: 30,
    tables: [
      { name: "Table 1", capacity: 2, quantity: 4 },
      { name: "Table 2", capacity: 4, quantity: 6 },
      { name: "Table 3", capacity: 6, quantity: 3 },
      { name: "Table 4", capacity: 8, quantity: 2 },
    ],
    specialRequests: ["High chair", "Wheelchair accessible", "Quiet area", "Window seat", "Outdoor seating"],
  });
  
  const [takeout, setTakeout] = useState({
    enabled: true,
    prepTimeMinutes: 20,
    preOrderEnabled: true,
    preOrderMinHours: 1,
    preOrderMaxDays: 7,
    pickupInstructions: "",
    deliveryEnabled: false,
    deliveryRadius: 5,
    deliveryFee: 5,
    minimumOrder: 15,
  });

  // Get auth token
  const getToken = () => sessionStorage.getItem("argus_trial_token") || localStorage.getItem("admin_token");

  // Load existing data
  useEffect(() => {
    const loadRestaurantData = async () => {
      const token = getToken();
      if (!token) return;
      
      try {
        const res = await fetch(`${API_URL}/api/restaurant/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.profile) setProfile(data.profile);
          if (data.hours) setHours(data.hours);
          if (data.menu) setMenu(data.menu);
          if (data.reservations) setReservations(data.reservations);
          if (data.takeout) setTakeout(data.takeout);
        }
      } catch (err) {
        console.error("Failed to load restaurant data:", err);
      }
    };
    loadRestaurantData();
  }, []);

  // Save current step data
  const saveStepData = async () => {
    setSaving(true);
    setError("");
    const token = getToken();
    
    try {
      const res = await fetch(`${API_URL}/api/restaurant/onboarding`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          step: currentStep,
          profile,
          hours,
          menu,
          reservations,
          takeout,
        })
      });
      
      if (!res.ok) throw new Error("Failed to save");
      setSuccess("Saved successfully!");
      setTimeout(() => setSuccess(""), 2000);
    } catch (err) {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Add menu item
  const addMenuItem = (categoryIndex) => {
    const newMenu = { ...menu };
    newMenu.categories[categoryIndex].items.push({
      name: "",
      description: "",
      price: "",
      prepTime: 15,
      dietary: [],
      available: true,
    });
    setMenu(newMenu);
  };

  // Remove menu item
  const removeMenuItem = (categoryIndex, itemIndex) => {
    const newMenu = { ...menu };
    newMenu.categories[categoryIndex].items.splice(itemIndex, 1);
    setMenu(newMenu);
  };

  // Update menu item
  const updateMenuItem = (categoryIndex, itemIndex, field, value) => {
    const newMenu = { ...menu };
    newMenu.categories[categoryIndex].items[itemIndex][field] = value;
    setMenu(newMenu);
  };

  // Add category
  const addCategory = () => {
    setMenu({
      ...menu,
      categories: [...menu.categories, { name: "New Category", items: [] }]
    });
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Restaurant Profile</h2>
            <p className="text-slate-400">Tell us about your restaurant so Echo can represent you perfectly.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">Restaurant Name *</label>
                <input
                  type="text"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white focus:border-gold focus:outline-none"
                  placeholder="e.g., The Golden Fork"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea
                  value={profile.description}
                  onChange={(e) => setProfile({ ...profile, description: e.target.value })}
                  className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white focus:border-gold focus:outline-none h-24"
                  placeholder="A brief description Echo will use when callers ask about your restaurant..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Cuisine Type *</label>
                <select
                  value={profile.cuisineType}
                  onChange={(e) => setProfile({ ...profile, cuisineType: e.target.value })}
                  className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white focus:border-gold focus:outline-none"
                >
                  <option value="">Select cuisine type</option>
                  {CUISINE_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Phone *</label>
                <input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white focus:border-gold focus:outline-none"
                  placeholder="(555) 123-4567"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">Street Address *</label>
                <input
                  type="text"
                  value={profile.address}
                  onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                  className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white focus:border-gold focus:outline-none"
                  placeholder="123 Main Street"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">City *</label>
                <input
                  type="text"
                  value={profile.city}
                  onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                  className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white focus:border-gold focus:outline-none"
                  placeholder="New York"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">State *</label>
                  <input
                    type="text"
                    value={profile.state}
                    onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                    className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white focus:border-gold focus:outline-none"
                    placeholder="NY"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">ZIP *</label>
                  <input
                    type="text"
                    value={profile.zipCode}
                    onChange={(e) => setProfile({ ...profile, zipCode: e.target.value })}
                    className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white focus:border-gold focus:outline-none"
                    placeholder="10001"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
                <input
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white focus:border-gold focus:outline-none"
                  placeholder="info@restaurant.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Website</label>
                <input
                  type="url"
                  value={profile.website}
                  onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                  className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white focus:border-gold focus:outline-none"
                  placeholder="https://www.yourrestaurant.com"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Hours & Contact</h2>
            <p className="text-slate-400">Set your operating hours so Echo knows when you're open.</p>
            
            <div className="space-y-4">
              {Object.entries(hours).map(([day, schedule]) => (
                <div key={day} className="flex items-center gap-4 p-4 bg-indigo-950/30 rounded-lg border border-gold/10">
                  <div className="w-28">
                    <span className="text-white font-medium capitalize">{day}</span>
                  </div>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!schedule.closed}
                      onChange={(e) => setHours({
                        ...hours,
                        [day]: { ...schedule, closed: !e.target.checked }
                      })}
                      className="w-4 h-4 accent-gold"
                    />
                    <span className="text-slate-400 text-sm">Open</span>
                  </label>
                  
                  {!schedule.closed && (
                    <>
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={schedule.open}
                          onChange={(e) => setHours({
                            ...hours,
                            [day]: { ...schedule, open: e.target.value }
                          })}
                          className="px-3 py-2 bg-indigo-950/50 border border-gold/20 rounded text-white text-sm"
                        />
                        <span className="text-slate-500">to</span>
                        <input
                          type="time"
                          value={schedule.close}
                          onChange={(e) => setHours({
                            ...hours,
                            [day]: { ...schedule, close: e.target.value }
                          })}
                          className="px-3 py-2 bg-indigo-950/50 border border-gold/20 rounded text-white text-sm"
                        />
                      </div>
                    </>
                  )}
                  
                  {schedule.closed && (
                    <span className="text-red-400 text-sm">Closed</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white">Menu Setup</h2>
                <p className="text-slate-400">Add your menu items so Echo can answer questions and take orders.</p>
              </div>
              <button
                onClick={addCategory}
                className="flex items-center gap-2 px-4 py-2 bg-gold/20 text-gold rounded-lg hover:bg-gold/30 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Category
              </button>
            </div>
            
            <div className="space-y-6">
              {menu.categories.map((category, catIndex) => (
                <div key={catIndex} className="p-4 bg-indigo-950/30 rounded-xl border border-gold/10">
                  <div className="flex items-center gap-4 mb-4">
                    <input
                      type="text"
                      value={category.name}
                      onChange={(e) => {
                        const newMenu = { ...menu };
                        newMenu.categories[catIndex].name = e.target.value;
                        setMenu(newMenu);
                      }}
                      className="text-lg font-bold bg-transparent text-white border-b border-gold/30 focus:border-gold focus:outline-none pb-1"
                    />
                    <button
                      onClick={() => addMenuItem(catIndex)}
                      className="flex items-center gap-1 text-sm text-gold hover:text-gold/80"
                    >
                      <Plus className="w-4 h-4" />
                      Add Item
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {category.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="grid grid-cols-12 gap-3 p-3 bg-indigo-950/50 rounded-lg">
                        <div className="col-span-4">
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => updateMenuItem(catIndex, itemIndex, "name", e.target.value)}
                            placeholder="Item name"
                            className="w-full px-3 py-2 bg-indigo-900/50 border border-gold/20 rounded text-white text-sm focus:border-gold focus:outline-none"
                          />
                        </div>
                        <div className="col-span-4">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateMenuItem(catIndex, itemIndex, "description", e.target.value)}
                            placeholder="Description"
                            className="w-full px-3 py-2 bg-indigo-900/50 border border-gold/20 rounded text-white text-sm focus:border-gold focus:outline-none"
                          />
                        </div>
                        <div className="col-span-2">
                          <div className="relative">
                            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                            <input
                              type="number"
                              value={item.price}
                              onChange={(e) => updateMenuItem(catIndex, itemIndex, "price", e.target.value)}
                              placeholder="Price"
                              className="w-full pl-7 pr-3 py-2 bg-indigo-900/50 border border-gold/20 rounded text-white text-sm focus:border-gold focus:outline-none"
                            />
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="relative">
                            <Timer className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                            <input
                              type="number"
                              value={item.prepTime}
                              onChange={(e) => updateMenuItem(catIndex, itemIndex, "prepTime", e.target.value)}
                              placeholder="Min"
                              className="w-full pl-7 pr-2 py-2 bg-indigo-900/50 border border-gold/20 rounded text-white text-sm focus:border-gold focus:outline-none"
                              title="Prep time in minutes"
                            />
                          </div>
                        </div>
                        <div className="col-span-1 flex items-center justify-center">
                          <button
                            onClick={() => removeMenuItem(catIndex, itemIndex)}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {category.items.length === 0 && (
                      <p className="text-slate-500 text-sm text-center py-4">No items yet. Click "Add Item" to get started.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Reservation Settings</h2>
            <p className="text-slate-400">Configure how Echo handles table reservations.</p>
            
            <div className="flex items-center gap-4 p-4 bg-indigo-950/30 rounded-lg border border-gold/10">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={reservations.enabled}
                  onChange={(e) => setReservations({ ...reservations, enabled: e.target.checked })}
                  className="w-5 h-5 accent-gold"
                />
                <span className="text-white font-medium">Enable Reservations</span>
              </label>
            </div>
            
            {reservations.enabled && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Min Party Size</label>
                    <input
                      type="number"
                      value={reservations.minPartySize}
                      onChange={(e) => setReservations({ ...reservations, minPartySize: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Max Party Size</label>
                    <input
                      type="number"
                      value={reservations.maxPartySize}
                      onChange={(e) => setReservations({ ...reservations, maxPartySize: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Slot Duration (min)</label>
                    <input
                      type="number"
                      value={reservations.slotDuration}
                      onChange={(e) => setReservations({ ...reservations, slotDuration: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white"
                      min="30"
                      step="15"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Advance Booking (days)</label>
                  <input
                    type="number"
                    value={reservations.advanceBookingDays}
                    onChange={(e) => setReservations({ ...reservations, advanceBookingDays: parseInt(e.target.value) })}
                    className="w-full max-w-xs px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white"
                    min="1"
                  />
                  <p className="text-slate-500 text-sm mt-1">How far in advance can guests book?</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-4">Table Configuration</label>
                  <div className="space-y-3">
                    {reservations.tables.map((table, index) => (
                      <div key={index} className="grid grid-cols-4 gap-4 p-3 bg-indigo-950/30 rounded-lg">
                        <input
                          type="text"
                          value={table.name}
                          onChange={(e) => {
                            const newTables = [...reservations.tables];
                            newTables[index].name = e.target.value;
                            setReservations({ ...reservations, tables: newTables });
                          }}
                          placeholder="Table name"
                          className="px-3 py-2 bg-indigo-950/50 border border-gold/20 rounded text-white text-sm"
                        />
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-500" />
                          <input
                            type="number"
                            value={table.capacity}
                            onChange={(e) => {
                              const newTables = [...reservations.tables];
                              newTables[index].capacity = parseInt(e.target.value);
                              setReservations({ ...reservations, tables: newTables });
                            }}
                            placeholder="Seats"
                            className="w-20 px-3 py-2 bg-indigo-950/50 border border-gold/20 rounded text-white text-sm"
                            min="1"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500 text-sm">Qty:</span>
                          <input
                            type="number"
                            value={table.quantity}
                            onChange={(e) => {
                              const newTables = [...reservations.tables];
                              newTables[index].quantity = parseInt(e.target.value);
                              setReservations({ ...reservations, tables: newTables });
                            }}
                            className="w-20 px-3 py-2 bg-indigo-950/50 border border-gold/20 rounded text-white text-sm"
                            min="1"
                          />
                        </div>
                        <button
                          onClick={() => {
                            const newTables = reservations.tables.filter((_, i) => i !== index);
                            setReservations({ ...reservations, tables: newTables });
                          }}
                          className="text-red-400 hover:text-red-300 justify-self-end"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        setReservations({
                          ...reservations,
                          tables: [...reservations.tables, { name: `Table ${reservations.tables.length + 1}`, capacity: 4, quantity: 1 }]
                        });
                      }}
                      className="flex items-center gap-2 text-gold hover:text-gold/80 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Table Type
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Takeout & Pre-Orders</h2>
            <p className="text-slate-400">Configure pickup orders and "food ready when I arrive" requests.</p>
            
            <div className="flex items-center gap-4 p-4 bg-indigo-950/30 rounded-lg border border-gold/10">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={takeout.enabled}
                  onChange={(e) => setTakeout({ ...takeout, enabled: e.target.checked })}
                  className="w-5 h-5 accent-gold"
                />
                <span className="text-white font-medium">Enable Takeout Orders</span>
              </label>
            </div>
            
            {takeout.enabled && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Default Prep Time (minutes)</label>
                    <input
                      type="number"
                      value={takeout.prepTimeMinutes}
                      onChange={(e) => setTakeout({ ...takeout, prepTimeMinutes: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white"
                      min="5"
                    />
                    <p className="text-slate-500 text-sm mt-1">Echo will use menu item prep times when available</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Minimum Order ($)</label>
                    <input
                      type="number"
                      value={takeout.minimumOrder}
                      onChange={(e) => setTakeout({ ...takeout, minimumOrder: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white"
                      min="0"
                    />
                  </div>
                </div>
                
                <div className="p-4 bg-indigo-950/30 rounded-lg border border-gold/10">
                  <label className="flex items-center gap-3 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      checked={takeout.preOrderEnabled}
                      onChange={(e) => setTakeout({ ...takeout, preOrderEnabled: e.target.checked })}
                      className="w-5 h-5 accent-gold"
                    />
                    <span className="text-white font-medium">Enable Pre-Orders</span>
                    <span className="text-slate-500 text-sm">("I want my food ready at 6pm")</span>
                  </label>
                  
                  {takeout.preOrderEnabled && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Minimum Hours Ahead</label>
                        <input
                          type="number"
                          value={takeout.preOrderMinHours}
                          onChange={(e) => setTakeout({ ...takeout, preOrderMinHours: parseInt(e.target.value) })}
                          className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Maximum Days Ahead</label>
                        <input
                          type="number"
                          value={takeout.preOrderMaxDays}
                          onChange={(e) => setTakeout({ ...takeout, preOrderMaxDays: parseInt(e.target.value) })}
                          className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white"
                          min="1"
                        />
                      </div>
                    </div>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Pickup Instructions</label>
                  <textarea
                    value={takeout.pickupInstructions}
                    onChange={(e) => setTakeout({ ...takeout, pickupInstructions: e.target.value })}
                    className="w-full px-4 py-3 bg-indigo-950/50 border border-gold/20 rounded-lg text-white h-24"
                    placeholder="e.g., Park in the designated pickup spots. Call us when you arrive and we'll bring your order out."
                  />
                </div>
              </>
            )}
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-white">Review & Launch</h2>
            <p className="text-slate-400">Review your settings and activate your AI supervisors.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Profile Summary */}
              <div className="p-6 bg-indigo-950/30 rounded-xl border border-gold/20">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-6 h-6 text-gold" />
                  <h3 className="text-lg font-bold text-white">Restaurant Profile</h3>
                  {profile.name && <CheckCircle2 className="w-5 h-5 text-emerald-400 ml-auto" />}
                </div>
                <div className="space-y-2 text-sm">
                  <p className="text-white font-medium">{profile.name || "Not set"}</p>
                  <p className="text-slate-400">{profile.cuisineType || "No cuisine type"}</p>
                  <p className="text-slate-400">{profile.address ? `${profile.address}, ${profile.city}` : "No address"}</p>
                  <p className="text-slate-400">{profile.phone || "No phone"}</p>
                </div>
              </div>
              
              {/* Hours Summary */}
              <div className="p-6 bg-indigo-950/30 rounded-xl border border-gold/20">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-6 h-6 text-gold" />
                  <h3 className="text-lg font-bold text-white">Operating Hours</h3>
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 ml-auto" />
                </div>
                <div className="space-y-1 text-sm">
                  {Object.entries(hours).slice(0, 4).map(([day, schedule]) => (
                    <p key={day} className="text-slate-400">
                      <span className="capitalize w-20 inline-block">{day}:</span>
                      {schedule.closed ? "Closed" : `${schedule.open} - ${schedule.close}`}
                    </p>
                  ))}
                  <p className="text-slate-500">...</p>
                </div>
              </div>
              
              {/* Menu Summary */}
              <div className="p-6 bg-indigo-950/30 rounded-xl border border-gold/20">
                <div className="flex items-center gap-3 mb-4">
                  <UtensilsCrossed className="w-6 h-6 text-gold" />
                  <h3 className="text-lg font-bold text-white">Menu</h3>
                  {menu.categories.some(c => c.items.length > 0) ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 ml-auto" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-400 ml-auto" />
                  )}
                </div>
                <div className="space-y-1 text-sm">
                  <p className="text-white">{menu.categories.length} categories</p>
                  <p className="text-slate-400">
                    {menu.categories.reduce((acc, c) => acc + c.items.length, 0)} menu items
                  </p>
                </div>
              </div>
              
              {/* Reservations Summary */}
              <div className="p-6 bg-indigo-950/30 rounded-xl border border-gold/20">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-6 h-6 text-gold" />
                  <h3 className="text-lg font-bold text-white">Reservations</h3>
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 ml-auto" />
                </div>
                <div className="space-y-1 text-sm">
                  <p className="text-white">{reservations.enabled ? "Enabled" : "Disabled"}</p>
                  {reservations.enabled && (
                    <>
                      <p className="text-slate-400">{reservations.tables.length} table types</p>
                      <p className="text-slate-400">Party size: {reservations.minPartySize}-{reservations.maxPartySize}</p>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            {/* Supervisor Status */}
            <div className="p-6 bg-gradient-to-br from-gold/10 to-transparent rounded-xl border border-gold/30">
              <h3 className="text-lg font-bold text-white mb-4">Supervisor Activation</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { name: "Echo", role: "The Ears", ready: !!profile.name },
                  { name: "Apollo", role: "The Eyes", ready: true },
                  { name: "Athena", role: "The Brain", ready: menu.categories.some(c => c.items.length > 0) },
                  { name: "Atlas", role: "The Hands", ready: !!profile.name },
                ].map((sup) => (
                  <div key={sup.name} className={`p-4 rounded-lg border ${sup.ready ? 'bg-emerald-900/20 border-emerald-500/30' : 'bg-amber-900/20 border-amber-500/30'}`}>
                    <p className="text-white font-bold">{sup.name}</p>
                    <p className="text-slate-400 text-sm">{sup.role}</p>
                    <p className={`text-sm mt-2 ${sup.ready ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {sup.ready ? '✓ Ready' : '⚠ Needs data'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#030B2C] py-8">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-white">Restaurant Onboarding</h1>
          <p className="text-slate-400 mt-2">Set up your restaurant so your AI supervisors can work perfectly.</p>
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8 overflow-x-auto pb-4">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => setCurrentStep(step.id)}
                  className={`flex flex-col items-center min-w-[100px] transition-all ${
                    isActive ? 'scale-110' : ''
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-colors ${
                    isCompleted ? 'bg-emerald-500 text-white' :
                    isActive ? 'bg-gold text-indigo-900' :
                    'bg-indigo-900/50 text-slate-500'
                  }`}>
                    {isCompleted ? <CheckCircle2 className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                  </div>
                  <span className={`text-xs font-medium ${isActive ? 'text-gold' : 'text-slate-500'}`}>
                    {step.title}
                  </span>
                </button>
                
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    currentStep > step.id ? 'bg-emerald-500' : 'bg-indigo-900/50'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        
        {/* Error/Success Messages */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-4 p-4 bg-red-900/30 border border-red-500/50 rounded-lg text-red-300"
            >
              {error}
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-4 p-4 bg-emerald-900/30 border border-emerald-500/50 rounded-lg text-emerald-300"
            >
              {success}
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Step Content */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="bg-indigo-950/20 border border-gold/10 rounded-2xl p-8"
        >
          {renderStepContent()}
        </motion.div>
        
        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <button
            onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
            disabled={currentStep === 1}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              currentStep === 1 
                ? 'bg-slate-800 text-slate-500 cursor-not-allowed' 
                : 'bg-indigo-900/50 text-white hover:bg-indigo-900'
            }`}
          >
            <ArrowLeft className="w-5 h-5" />
            Previous
          </button>
          
          <div className="flex gap-4">
            <button
              onClick={saveStepData}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-900/50 text-white rounded-lg font-medium hover:bg-indigo-900 transition-colors"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              Save Progress
            </button>
            
            {currentStep < 6 ? (
              <button
                onClick={() => {
                  saveStepData();
                  setCurrentStep(currentStep + 1);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-gold text-indigo-900 rounded-lg font-bold hover:bg-gold/90 transition-colors"
              >
                Next Step
                <ArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={async () => {
                  await saveStepData();
                  // Activate supervisors
                  const token = getToken();
                  await fetch(`${API_URL}/api/restaurant/activate`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${token}`
                    }
                  });
                  window.location.href = "/trial/dashboard";
                }}
                className="flex items-center gap-2 px-8 py-3 bg-emerald-500 text-white rounded-lg font-bold hover:bg-emerald-600 transition-colors"
              >
                <CheckCircle2 className="w-5 h-5" />
                Activate Supervisors
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
